import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Sparkles,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Stethoscope,
  Lightbulb,
  Activity,
  BookOpen,
} from 'lucide-react';
import {
  GenieWindow,
  GenieQuestion,
  GenieTiebreaker,
  GenieDiagnosisCatalogEntry,
  SuspicionCategory,
  SUSPICION_LABELS,
  GENIE_DIAGNOSIS_CATALOG,
  UNKNOWN_SUSPICION_ID,
} from '../data/genieQuestions';
import {
  GenieAnswers,
  LiveDiagnosis,
  evaluateGenie,
  getBestNextQuestion,
  getGenieWarnings,
  getGenieSectionSuggestions,
  compareSuspicionWithLive,
  getDiagnosisName,
  getActiveQuestionsForWindow,
  getVisibleGenieQuestions,
  pruneOrphanGenieAnswers,
} from '../services/genieEngine';
import {
  getKnowledgeForLiveDiagnosis,
  getKnowledgeForOrientation,
  getKnowledgeForTiebreaker,
  getTemporalCourseFromGenieAnswer,
  type GenieConversationContext,
  type GenieKnowledgeBlock,
} from '../services/genieKnowledgeService';

// ─── Constants ────────────────────────────────────────────────────────────────

const WINDOW_LABELS: Record<GenieWindow, string> = {
  symptoms: 'Síntomas',
  ncs: 'NCS',
  emg: 'EMG',
};

const WINDOWS: GenieWindow[] = ['symptoms', 'ncs', 'emg'];

/** Clases de Tailwind para las barras por posición en el ranking. */
const BAR_COLORS = [
  'bg-indigo-500',
  'bg-indigo-400/60',
  'bg-slate-600',
  'bg-slate-700',
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface FloatingGeniePanelProps {
  /** Respuestas actuales del usuario (controlado desde el padre). */
  answers: GenieAnswers;
  /** Callback cuando el usuario cambia una respuesta. */
  onChange: (answers: GenieAnswers) => void;
  /** Ventana actual del workflow. `null` = modo resumen (análisis / reporte). */
  currentWindow: GenieWindow | null;
  /** Desempates activos detectados en el análisis (sólo se muestra badge). */
  activeTiebreakers?: GenieTiebreaker[];
  /** Diagnósticos detectados en vivo por el motor (solo informativo). */
  liveDiagnoses?: LiveDiagnosis[];
  /** Sospechas iniciales declaradas por el médico (ids del catálogo). */
  initialSuspicions?: string[];
  /** Callback al cambiar la lista de sospechas iniciales. */
  onInitialSuspicionsChange?: (ids: string[]) => void;
}

/** Agrupa el catálogo de diagnósticos por categoría para el selector. */
const CATALOG_BY_CATEGORY: Array<{
  category: SuspicionCategory;
  entries: GenieDiagnosisCatalogEntry[];
}> = (['neuropatico', 'miopatico', 'unm', 'central'] as SuspicionCategory[])
  .map(category => ({
    category,
    entries: GENIE_DIAGNOSIS_CATALOG.filter(d => d.category === category),
  }))
  .filter(group => group.entries.length > 0);

/** Renderiza bloques de conocimiento clínico para el panel */
function GenieKnowledgeBlocks({
  context,
  compact = false,
  headlineOnly = false,
}: {
  context: GenieConversationContext;
  compact?: boolean;
  headlineOnly?: boolean;
}) {
  const blocks = headlineOnly
    ? context.blocks.filter(b => b.type === 'headline')
    : context.blocks;
  return (
    <div className={`space-y-1.5 ${compact ? 'mt-1 ml-0' : 'mt-2'}`}>
      {blocks.map((block, i) => (
        <KnowledgeBlock key={`${context.entryId}-${i}`} block={block} compact={compact} />
      ))}
    </div>
  );
}

function KnowledgeBlock({
  block,
  compact,
}: {
  block: GenieKnowledgeBlock;
  compact?: boolean;
}) {
  switch (block.type) {
    case 'headline':
      return (
        <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-gray-400 leading-relaxed italic`}>
          {block.content as string}
        </p>
      );
    case 'pearl':
      return (
        <p className={`flex items-start gap-1.5 ${compact ? 'text-[10px]' : 'text-[11px]'} text-amber-200/80 leading-relaxed`}>
          <Lightbulb className="w-3 h-3 shrink-0 mt-0.5 opacity-70" />
          <span>{block.content as string}</span>
        </p>
      );
    case 'bullets':
    case 'differential':
    case 'next_steps':
      return (
        <ul className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-gray-400 space-y-0.5 list-disc list-inside leading-relaxed`}>
          {(block.content as string[]).map((line, j) => (
            <li key={j}>{line}</li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const FloatingGeniePanel: React.FC<FloatingGeniePanelProps> = ({
  answers,
  onChange,
  currentWindow,
  activeTiebreakers = [],
  liveDiagnoses = [],
  initialSuspicions = [],
  onInitialSuspicionsChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showSuspicionPicker, setShowSuspicionPicker] = useState(false);

  // Reiniciar "ver todas" al cambiar de ventana
  useEffect(() => {
    setShowAllQuestions(false);
  }, [currentWindow]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const evaluation = useMemo(() => evaluateGenie(answers), [answers]);
  const warnings = useMemo(
    () => getGenieWarnings(answers, { liveDiagnoses, evaluation }),
    [answers, liveDiagnoses, evaluation]
  );

  const composite = evaluation.compositeRationale;
  const hasLiveDiagnoses = liveDiagnoses.length > 0;

  const activeQuestionTotal = useMemo(
    () => getVisibleGenieQuestions(answers).length,
    [answers]
  );

  const totalAnswered = useMemo(
    () => getVisibleGenieQuestions(answers).filter(q => answers[q.id]).length,
    [answers]
  );

  const sectionSuggestions = useMemo(
    () => (currentWindow ? getGenieSectionSuggestions(currentWindow, evaluation) : []),
    [currentWindow, evaluation]
  );

  const suspicionComparison = useMemo(
    () => compareSuspicionWithLive(initialSuspicions, liveDiagnoses),
    [initialSuspicions, liveDiagnoses]
  );

  const temporalCourse = useMemo(
    () => getTemporalCourseFromGenieAnswer(answers['curso_temporal']),
    [answers]
  );

  const topLiveKnowledge = useMemo(() => {
    if (!liveDiagnoses.length) return null;
    const top = liveDiagnoses[0];
    return getKnowledgeForLiveDiagnosis(top.id, {
      temporalCourse,
      confidence: top.confidence,
    });
  }, [liveDiagnoses, temporalCourse]);

  const tiebreakerKnowledge = useMemo(
    () =>
      activeTiebreakers.length > 0
        ? getKnowledgeForTiebreaker(activeTiebreakers[0].id)
        : null,
    [activeTiebreakers]
  );

  const orientationKnowledgeMap = useMemo(() => {
    if (!composite?.compatibleDiagnoses.length) return new Map<string, GenieConversationContext>();
    const map = new Map<string, GenieConversationContext>();
    for (const entry of composite.compatibleDiagnoses) {
      const ctx = getKnowledgeForOrientation(entry.id, { temporalCourse });
      if (ctx) map.set(entry.id, ctx);
    }
    return map;
  }, [composite, temporalCourse]);

  const toggleSuspicion = useCallback(
    (id: string) => {
      if (!onInitialSuspicionsChange) return;
      const isUnknown = id === UNKNOWN_SUSPICION_ID;
      let next: string[];
      if (initialSuspicions.includes(id)) {
        next = initialSuspicions.filter(s => s !== id);
      } else if (isUnknown) {
        // "No lo sé" es excluyente: limpia el resto.
        next = [UNKNOWN_SUSPICION_ID];
      } else {
        next = [...initialSuspicions.filter(s => s !== UNKNOWN_SUSPICION_ID), id];
      }
      onInitialSuspicionsChange(next);
    },
    [initialSuspicions, onInitialSuspicionsChange]
  );

  const sessionProgress = useMemo(
    () =>
      WINDOWS.map(w => {
        const qs = getActiveQuestionsForWindow(answers, w);
        return {
          window: w,
          label: WINDOW_LABELS[w],
          total: qs.length,
          answered: qs.filter(q => answers[q.id]).length,
        };
      }),
    [answers]
  );

  const currentWindowQuestions = useMemo(
    () => (currentWindow ? getActiveQuestionsForWindow(answers, currentWindow) : []),
    [currentWindow, answers]
  );

  const nextQuestion = useMemo(
    () =>
      currentWindow
        ? getBestNextQuestion(answers, evaluation, currentWindow)
        : null,
    [answers, evaluation, currentWindow]
  );

  const alertCount = useMemo(() => {
    let n = evaluation.isAmbiguous ? 1 : 0;
    n += warnings.length;
    n += activeTiebreakers.length;
    if (suspicionComparison.unexpected.length > 0) n += 1;
    return n;
  }, [
    evaluation.isAmbiguous,
    warnings.length,
    activeTiebreakers.length,
    suspicionComparison.unexpected.length,
  ]);

  const topBars = useMemo(
    () => evaluation.ranking.filter(s => s.rawScore > 0).slice(0, 3),
    [evaluation.ranking]
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSelect = useCallback(
    (question: GenieQuestion, optionId: string) => {
      const next = { ...answers };
      if (next[question.id] === optionId) {
        delete next[question.id];
      } else {
        next[question.id] = optionId;
      }
      onChange(pruneOrphanGenieAnswers(next));
    },
    [answers, onChange]
  );

  const handleReset = useCallback(() => {
    onChange({});
  }, [onChange]);

  // ── Question renderer ─────────────────────────────────────────────────────

  const renderQuestion = (question: GenieQuestion) => {
    const selectedId = answers[question.id];
    return (
      <fieldset key={question.id}>
        <legend className="text-sm font-medium text-gray-100 leading-snug mb-1">
          {question.text}
        </legend>
        {question.helper && (
          <p className="text-xs text-gray-400 mb-1.5">{question.helper}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {question.options.map(opt => {
            const isSelected = selectedId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handleSelect(question, opt.id)}
                className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-500'
                      : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-indigo-500 hover:text-indigo-200'
                  }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  };

  // ── Minimized bubble ──────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div
          className="bg-gray-900/95 border border-gray-700 rounded-2xl shadow-2xl backdrop-blur-sm w-64 cursor-pointer select-none"
          role="button"
          tabIndex={0}
          aria-expanded={false}
          aria-label="Abrir asistente Genie"
          onClick={() => setIsOpen(true)}
          onKeyDown={e => e.key === 'Enter' && setIsOpen(true)}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-3.5 pt-2.5 pb-1.5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
              <span className="text-sm font-semibold text-indigo-300">Genie</span>
            </div>
            <div className="flex items-center gap-1.5">
              {alertCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-gray-900 text-[10px] font-bold px-1 leading-none">
                  {alertCount}
                </span>
              )}
              <span className="text-[11px] text-gray-500">
                {totalAnswered}/{activeQuestionTotal}
              </span>
              <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
            </div>
          </div>

          {/* Mini bars */}
          <div className="px-3.5 pb-2.5 space-y-1">
            {evaluation.topSuspicion ? (
              topBars.slice(0, 2).map((score, idx) => (
                <div key={score.category} className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 w-28 shrink-0 truncate">
                    {score.label}
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-800 rounded overflow-hidden">
                    <div
                      className={`h-full ${BAR_COLORS[idx]}`}
                      style={{ width: `${Math.round(score.normalized * 100)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 w-7 text-right">
                    {Math.round(score.normalized * 100)}%
                  </span>
                </div>
              ))
            ) : (
              <p className="text-[11px] text-gray-500 italic">Sin respuestas aún</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded panel ────────────────────────────────────────────────────────

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 md:w-96 max-h-[82vh] flex flex-col bg-gray-900/97 border border-gray-700 rounded-2xl shadow-2xl backdrop-blur-sm">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/60 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-indigo-300 leading-tight truncate">
              Genie · Orientación diagnóstica
            </h3>
            <p className="text-[11px] text-gray-500">
              {totalAnswered}/{activeQuestionTotal} preguntas respondidas
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {alertCount > 0 && (
            <span className="flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-gray-900 text-[10px] font-bold px-1 leading-none">
              {alertCount}
            </span>
          )}
          <button
            type="button"
            onClick={handleReset}
            title="Reiniciar respuestas"
            aria-label="Reiniciar todas las respuestas del Genie"
            className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            title="Minimizar"
            aria-label="Minimizar panel del Genie"
            aria-expanded={true}
            className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto flex-1 px-4 py-3 space-y-4 min-h-0">

        {/* ── Suspicion bars ─────────────────────────────────────────────── */}
        {evaluation.topSuspicion ? (
          <div className="space-y-1.5">
            {topBars.map((score, idx) => (
              <div key={score.category} className="flex items-center gap-2">
                <span
                  className="text-xs text-gray-400 w-40 shrink-0 truncate"
                  title={score.label}
                >
                  {score.label}
                </span>
                <div className="flex-1 h-2 bg-gray-800 rounded overflow-hidden">
                  <div
                    className={`h-full ${BAR_COLORS[idx]} transition-all duration-300`}
                    style={{ width: `${Math.round(score.normalized * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-8 text-right">
                  {Math.round(score.normalized * 100)}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500 italic">
            Responde las primeras preguntas para ver la orientación topográfica.
          </p>
        )}

        {/* ── Alerts ─────────────────────────────────────────────────────── */}
        {evaluation.isAmbiguous && (
          <div className="flex items-start gap-2 text-amber-300 text-xs bg-amber-950/50 border border-amber-700/50 rounded-lg p-2.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Diferencial estrecho. La próxima pregunta prioriza las que mejor
              separan las dos hipótesis.
            </span>
          </div>
        )}
        {warnings.map(w => (
          <div
            key={w}
            className="flex items-start gap-2 text-yellow-300 text-xs bg-yellow-950/40 border border-yellow-700/50 rounded-lg p-2.5"
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{w}</span>
          </div>
        ))}

        {/* ── Diagnósticos en vivo (prioridad sobre orientación clínica) ───── */}
        {hasLiveDiagnoses && (
          <div className="border-t border-gray-700/60 pt-3">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-500/90 uppercase tracking-wider mb-2">
              <Activity className="w-3 h-3" /> Diagnósticos en vivo
            </p>
            <div className="space-y-2">
              {liveDiagnoses.map(dx => {
                const pct = Math.round(dx.confidence * 100);
                const isConfirmed = suspicionComparison.confirmed.includes(dx.id);
                const isUnexpected = suspicionComparison.unexpected.includes(dx.id);
                const dxKnowledge = getKnowledgeForLiveDiagnosis(dx.id, {
                  temporalCourse,
                  confidence: dx.confidence,
                });
                return (
                  <div key={dx.id} className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-300 flex-1 truncate" title={dx.name}>
                        {dx.name}
                        {isConfirmed && (
                          <span className="ml-1 text-[10px] text-green-400">✓ esperado</span>
                        )}
                        {isUnexpected && (
                          <span className="ml-1 text-[10px] text-amber-400">nuevo</span>
                        )}
                      </span>
                      <div className="w-20 h-2 bg-gray-800 rounded overflow-hidden shrink-0">
                        <div
                          className={`h-full ${
                            pct >= 60 ? 'bg-emerald-500' : pct >= 40 ? 'bg-indigo-500' : 'bg-slate-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-gray-400 w-8 text-right shrink-0">{pct}%</span>
                    </div>
                    {dxKnowledge && (
                      <GenieKnowledgeBlocks
                        context={dxKnowledge}
                        compact
                        headlineOnly={liveDiagnoses.length > 1}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {topLiveKnowledge && liveDiagnoses.length > 1 && (
              <div className="mt-3 pt-2 border-t border-gray-700/40">
                <p className="flex items-center gap-1.5 text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">
                  <BookOpen className="w-3 h-3" />
                  Más sobre {topLiveKnowledge.name}
                </p>
                <GenieKnowledgeBlocks context={topLiveKnowledge} />
              </div>
            )}

            {suspicionComparison.unsupported.length > 0 && (
              <p className="mt-2 text-[11px] text-gray-400">
                Sospecha inicial aún sin soporte electrofisiológico:{' '}
                <span className="text-gray-300">
                  {suspicionComparison.unsupported.map(getDiagnosisName).join(', ')}
                </span>
                .
              </p>
            )}
            {suspicionComparison.declaredUnknown && (
              <p className="mt-2 text-[11px] text-gray-500 italic">
                Sin sospecha previa: el motor sugiere las hipótesis de arriba para orientar.
              </p>
            )}
          </div>
        )}

        {/* ── Razonamiento clínico compuesto ──────────────────────────────── */}
        {composite && (
          <div
            className={`border-t border-gray-700/60 pt-3 space-y-2 ${
              hasLiveDiagnoses ? 'opacity-80' : ''
            }`}
          >
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
              <Lightbulb className="w-3 h-3" />
              {hasLiveDiagnoses ? 'Contexto clínico' : 'Razonamiento clínico'}
            </p>
            <p className="text-xs text-gray-300 leading-relaxed">{composite.summary}</p>
            {composite.secondary.map((msg, i) => (
              <p key={i} className="text-[11px] text-gray-400 leading-relaxed pl-2 border-l-2 border-gray-700">
                {msg}
              </p>
            ))}
            {composite.nextStepHint && !hasLiveDiagnoses && (
              <p className="text-[11px] text-indigo-400/90">{composite.nextStepHint}</p>
            )}
          </div>
        )}

        {/* ── Diferencial orientador (sin datos electrofisiológicos) ──────── */}
        {!hasLiveDiagnoses &&
          composite &&
          composite.compatibleDiagnoses.length > 0 && (
            <div className="border-t border-gray-700/60 pt-3">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">
                Diferencial orientador
              </p>
              <p className="text-[10px] text-gray-500 mb-2 italic">
                Hipótesis a considerar; confirmar con NCS/EMG.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {composite.compatibleDiagnoses.map(entry => {
                  const hint = orientationKnowledgeMap.get(entry.id)?.blocks[0]?.content;
                  const title =
                    typeof hint === 'string'
                      ? hint
                      : 'Hipótesis orientadora; confirmar con NCS/EMG';
                  return (
                    <span
                      key={entry.id}
                      title={title}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gray-800 text-gray-300 border border-gray-600/80 cursor-help"
                    >
                      {entry.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

        {activeTiebreakers.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-amber-300 text-xs bg-amber-950/40 border border-amber-700/40 rounded-lg p-2.5">
              <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {activeTiebreakers.length === 1
                  ? 'Hay un desempate pendiente en Análisis Diagnóstico.'
                  : `Hay ${activeTiebreakers.length} desempates pendientes en Análisis Diagnóstico.`}
              </span>
            </div>
            {tiebreakerKnowledge && (
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-2.5">
                <p className="text-[10px] font-medium text-amber-400/90 uppercase tracking-wider mb-1">
                  {tiebreakerKnowledge.name}
                </p>
                <GenieKnowledgeBlocks context={tiebreakerKnowledge} compact />
              </div>
            )}
          </div>
        )}

        {/* ── Sospecha inicial del médico ─────────────────────────────────── */}
        <div className="border-t border-gray-700/60 pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
              <Stethoscope className="w-3 h-3" /> Sospecha inicial
            </p>
            {onInitialSuspicionsChange && (
              <button
                type="button"
                onClick={() => setShowSuspicionPicker(v => !v)}
                className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                {showSuspicionPicker ? 'Listo' : 'Editar'}
              </button>
            )}
          </div>

          {/* Chips seleccionados */}
          {initialSuspicions.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {initialSuspicions.map(id => (
                <span
                  key={id}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-900/50 text-indigo-200 border border-indigo-700/50"
                >
                  {getDiagnosisName(id)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 italic mb-1.5">
              Sin sospecha declarada. Pulsa «Editar» para registrar el diferencial inicial.
            </p>
          )}

          {/* Selector */}
          {showSuspicionPicker && onInitialSuspicionsChange && (
            <div className="space-y-2 bg-gray-800/40 rounded-lg p-2.5 border border-gray-700/40">
              <button
                type="button"
                aria-pressed={initialSuspicions.includes(UNKNOWN_SUSPICION_ID)}
                onClick={() => toggleSuspicion(UNKNOWN_SUSPICION_ID)}
                className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                  initialSuspicions.includes(UNKNOWN_SUSPICION_ID)
                    ? 'bg-indigo-600 text-white border-indigo-500'
                    : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-indigo-500'
                }`}
              >
                No lo sé / desconozco
              </button>
              {CATALOG_BY_CATEGORY.map(group => (
                <div key={group.category}>
                  <p className="text-[10px] text-gray-500 mb-1">
                    {SUSPICION_LABELS[group.category]}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.entries.map(entry => {
                      const isSel = initialSuspicions.includes(entry.id);
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          aria-pressed={isSel}
                          onClick={() => toggleSuspicion(entry.id)}
                          className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                            isSel
                              ? 'bg-indigo-600 text-white border-indigo-500'
                              : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-indigo-500 hover:text-indigo-200'
                          }`}
                        >
                          {entry.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Sugerencias por sección ─────────────────────────────────────── */}
        {sectionSuggestions.length > 0 && (
          <div className="border-t border-gray-700/60 pt-3">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
              <Lightbulb className="w-3 h-3" /> Sugerencias para esta sección
            </p>
            <ul className="space-y-1.5">
              {sectionSuggestions.map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-xs text-gray-300 bg-gray-800/40 border border-gray-700/40 rounded-lg px-2.5 py-2"
                >
                  <ArrowRight className="w-3 h-3 text-indigo-400 shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Session timeline ────────────────────────────────────────────── */}
        <div>
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">
            Progreso por sección
          </p>
          <div className="space-y-1.5">
            {sessionProgress.map(({ window: w, label, total, answered }) => {
              const isActive = w === currentWindow;
              const isDone = answered === total && total > 0;
              return (
                <div
                  key={w}
                  className={`flex items-center gap-2 ${
                    isActive ? 'text-indigo-300' : 'text-gray-500'
                  }`}
                >
                  <span
                    className={`text-xs w-16 shrink-0 ${
                      isActive ? 'font-medium' : ''
                    }`}
                  >
                    {label}
                  </span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: total }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i < answered
                            ? isDone
                              ? 'bg-green-500'
                              : isActive
                              ? 'bg-indigo-500'
                              : 'bg-indigo-400/50'
                            : isActive
                            ? 'bg-gray-600'
                            : 'bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] ml-auto shrink-0">
                    {answered}/{total}
                  </span>
                  {isDone && (
                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Questions for current window ────────────────────────────────── */}
        {currentWindow && currentWindowQuestions.length > 0 && (
          <div className="border-t border-gray-700/60 pt-3 space-y-3">
            {showAllQuestions ? (
              <>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                  Todas · {WINDOW_LABELS[currentWindow]}
                </p>
                <div className="space-y-4">
                  {currentWindowQuestions.map(q => renderQuestion(q))}
                </div>
              </>
            ) : nextQuestion ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-indigo-400" />
                  <p className="text-[11px] font-medium text-indigo-400 uppercase tracking-wider">
                    Siguiente pregunta
                    {evaluation.isAmbiguous && (
                      <span className="ml-1 normal-case text-amber-400 font-normal">
                        · máx. discriminación
                      </span>
                    )}
                  </p>
                </div>
                {renderQuestion(nextQuestion)}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Sección completada</span>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowAllQuestions(v => !v)}
              className="text-xs text-gray-500 hover:text-indigo-400 transition-colors"
            >
              {showAllQuestions
                ? '↑ Mostrar solo siguiente pregunta'
                : '↓ Ver todas las preguntas de esta sección'}
            </button>
          </div>
        )}

        {/* ── Disclaimer ─────────────────────────────────────────────────── */}
        <p className="text-[10px] text-gray-600 leading-snug border-t border-gray-800 pt-3">
          El Genie orienta la sospecha topográfica; no sustituye el análisis
          electrofisiológico ni la correlación clínica final.
        </p>
      </div>
    </div>
  );
};

export default FloatingGeniePanel;
